import { Command } from 'commander';
import { readFileSync, writeFileSync } from 'node:fs';
import { basename } from 'node:path';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { getBase, getFormat } from '../helpers.js';
import { confirm } from '../prompt.js';

export const filesCommand = new Command('files')
  .description('Manage files (path-addressed, versioned content) within a file type');

filesCommand.command('put <file_type> <path>')
  .description('Upload a local file to a path (e.g. rekor files put reports q3/summary.pdf --file ./summary.pdf)')
  .requiredOption('--file <local>', 'Local file to upload')
  .option('--content-type <type>', 'MIME type', 'application/octet-stream')
  .option('--metadata <json>', 'Structured metadata, JSON (inline or @filename)')
  .option('--label <label>', 'Free-string tag for the content version this upload creates (e.g. "proposal 1")')
  .option('--if-match <sha256>', 'Compare-and-set: upload only if the current content hash matches')
  .action(async function (this: Command, fileType: string, path: string, opts: { file: string; contentType: string; metadata?: string; label?: string; ifMatch?: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const body = readFileSync(opts.file);
    const headers: Record<string, string> = {};
    if (opts.ifMatch) headers['If-Match'] = opts.ifMatch;
    if (opts.label) headers['X-Rekor-Label'] = opts.label;
    if (opts.metadata) headers['X-Rekor-Metadata'] = opts.metadata.startsWith('@') ? readFileSync(opts.metadata.slice(1), 'utf8') : opts.metadata;
    const url = `${client.baseUrl}/v1/${ws}/files/${fileType}/${path.split('/').map(encodeURIComponent).join('/')}`;
    const data = await client.uploadFile(url, body, opts.contentType, headers);
    console.log(formatOutput(data, getFormat(this)));
  });

filesCommand.command('get <file_type> <path>')
  .description('Download a file (or --meta for metadata only). --version N reads a specific retained content version.')
  .option('--output <local>', 'Local file to save to (defaults to the file basename)')
  .option('--meta', 'Print metadata JSON instead of downloading the content')
  .option('--version <n>', 'Read a specific content version (default: latest)')
  .action(async function (this: Command, fileType: string, path: string, opts: { output?: string; meta?: boolean; version?: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const encoded = path.split('/').map(encodeURIComponent).join('/');
    const versionQs = opts.version !== undefined ? `version=${encodeURIComponent(opts.version)}` : '';
    if (opts.meta) {
      const qs = ['meta=1', versionQs].filter(Boolean).join('&');
      const data = await client.request('GET', `/v1/${ws}/files/${fileType}/${encoded}?${qs}`);
      console.log(formatOutput(data, getFormat(this)));
      return;
    }
    const { buffer } = await client.downloadFile(`/v1/${ws}/files/${fileType}/${encoded}${versionQs ? `?${versionQs}` : ''}`);
    const out = opts.output || basename(path);
    writeFileSync(out, Buffer.from(buffer));
    console.log(`Downloaded to ${out}`);
  });

filesCommand.command('history <file_type> <path>')
  .description('List the content versions of a file (newest first)')
  .option('--limit <n>', 'Max versions to return')
  .option('--offset <n>', 'Pagination offset')
  .action(async function (this: Command, fileType: string, path: string, opts: { limit?: string; offset?: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const encoded = path.split('/').map(encodeURIComponent).join('/');
    const qs = new URLSearchParams({ history: '1' });
    if (opts.limit) qs.set('limit', opts.limit);
    if (opts.offset) qs.set('offset', opts.offset);
    const data = await client.request('GET', `/v1/${ws}/files/${fileType}/${encoded}?${qs}`);
    console.log(formatOutput(data, getFormat(this)));
  });

filesCommand.command('diff <file_type> <path>')
  .description('Diff two content versions of a file — metadata delta plus a text content diff')
  .requiredOption('--from <n>', 'Base version number')
  .requiredOption('--to <n>', 'Target version number')
  .action(async function (this: Command, fileType: string, path: string, opts: { from: string; to: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const encoded = path.split('/').map(encodeURIComponent).join('/');
    const qs = new URLSearchParams({ diff: '1', from: opts.from, to: opts.to });
    const data = await client.request('GET', `/v1/${ws}/files/${fileType}/${encoded}?${qs}`) as DiffResponse;
    if (getFormat(this) === 'json') { console.log(formatOutput(data, 'json')); return; }
    renderFileDiff(fileType, path, opts.from, opts.to, data);
  });

interface DiffResponse {
  metadata_delta?: {
    content_changed?: boolean;
    size?: { from: number; to: number } | null;
    content_type?: { from: string; to: string } | null;
    path?: { from: string; to: string } | null;
    label?: { from: string; to: string } | null;
    metadata?: { added?: Record<string, unknown>; removed?: Record<string, unknown>; changed?: Record<string, { from: unknown; to: unknown }> };
  };
  content?: { changed?: boolean; kind?: string; lines?: Array<{ op: string; text: string }> };
}

function renderFileDiff(fileType: string, path: string, from: string, to: string, d: DiffResponse): void {
  console.log(`${fileType}/${path}: v${from} → v${to}`);
  const md = d.metadata_delta;
  if (md) {
    for (const f of ['path', 'size', 'content_type', 'label'] as const) {
      const change = md[f];
      if (change) console.log(`  ${f}: ${JSON.stringify(change.from)} → ${JSON.stringify(change.to)}`);
    }
    const meta = md.metadata;
    for (const [k, v] of Object.entries(meta?.added ?? {})) console.log(`  metadata + ${k}: ${JSON.stringify(v)}`);
    for (const k of Object.keys(meta?.removed ?? {})) console.log(`  metadata - ${k}`);
    for (const [k, v] of Object.entries(meta?.changed ?? {})) console.log(`  metadata ~ ${k}: ${JSON.stringify(v.from)} → ${JSON.stringify(v.to)}`);
  }
  const content = d.content;
  if (!content?.changed) { console.log('  content: unchanged'); return; }
  if (content.kind === 'text' && content.lines) {
    console.log('');
    for (const l of content.lines) console.log(`${l.op} ${l.text}`);
  } else {
    console.log(`  content: changed (${content.kind})`);
  }
}

filesCommand.command('list <file_type>')
  .description('List files by path prefix')
  .option('--prefix <path>', 'Path prefix filter (e.g. q3/)')
  .option('--depth <n>', 'Max folder depth below the prefix (1 = immediate children)')
  .option('--limit <n>', 'Max results')
  .option('--offset <n>', 'Pagination offset')
  .action(async function (this: Command, fileType: string, opts: { prefix?: string; depth?: string; limit?: string; offset?: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const qs = new URLSearchParams();
    if (opts.prefix) qs.set('prefix', opts.prefix);
    if (opts.depth) qs.set('depth', opts.depth);
    if (opts.limit) qs.set('limit', opts.limit);
    if (opts.offset) qs.set('offset', opts.offset);
    const q = qs.toString();
    const data = await client.request('GET', `/v1/${ws}/files/${fileType}${q ? `?${q}` : ''}`);
    console.log(formatOutput(data, getFormat(this)));
  });

filesCommand.command('mv <file_type> <from> <to>')
  .description('Rename/move a file (the blob is untouched)')
  .action(async function (this: Command, fileType: string, from: string, to: string) {
    const ws = getBase(this);
    const client = new ApiClient();
    const encoded = from.split('/').map(encodeURIComponent).join('/');
    const data = await client.request('PATCH', `/v1/${ws}/files/${fileType}/${encoded}`, { new_path: to });
    console.log(formatOutput(data, getFormat(this)));
  });

filesCommand.command('rm <file_type> <path>')
  .description('Delete a file')
  .option('-y, --yes', 'Skip confirmation prompt')
  .action(async function (this: Command, fileType: string, path: string, opts: { yes?: boolean }) {
    if (!opts.yes && !(await confirm(`Delete file "${fileType}/${path}"? This cannot be undone.`))) {
      console.log('Aborted');
      return;
    }
    const ws = getBase(this);
    const client = new ApiClient();
    const encoded = path.split('/').map(encodeURIComponent).join('/');
    await client.request('DELETE', `/v1/${ws}/files/${fileType}/${encoded}`);
    console.log('Deleted');
  });

filesCommand.command('mount <file_type>')
  .description('Mint an S3 credential so any harness can mount this file type as a filesystem (s3fs, cloud mount SDKs). Read-only by default; the secret is shown once.')
  .option('--expires-in <seconds>', 'Credential lifetime in seconds (default 30 days, max 90)')
  .option('--mode <mode>', 'read_only (default) or read_write — the whole-bucket mode when no --path is given')
  .option('--path <prefix>', 'Confine to a path prefix (repeatable); optionally suffix :read_only|:read_write per prefix (e.g. drafts/:read_write)', (v: string, acc: string[]) => { acc.push(v); return acc; }, [])
  .action(async function (this: Command, fileType: string, opts: { expiresIn?: string; mode?: string; path: string[] }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const body: Record<string, unknown> = { file_type: fileType };
    if (opts.mode) {
      if (opts.mode !== 'read_only' && opts.mode !== 'read_write') {
        console.error("--mode must be 'read_only' or 'read_write'.");
        process.exit(1);
      }
      body.mode = opts.mode;
    }
    if (opts.path.length > 0) {
      body.paths = opts.path.map((p) => {
        const m = p.match(/^(.*):(read_only|read_write)$/);
        return m ? { prefix: m[1], mode: m[2] } : { prefix: p, mode: opts.mode ?? 'read_only' };
      });
    }
    if (opts.expiresIn !== undefined) {
      const n = Number(opts.expiresIn);
      if (!Number.isFinite(n)) {
        console.error('--expires-in must be a number of seconds.');
        process.exit(1);
      }
      body.expires_in = n;
    }
    const data = await client.request('POST', `/v1/${ws}/s3-credentials`, body);
    console.log(formatOutput(data, getFormat(this)));
  });
